// Aufgeklappte SMS in der Inbox (Reiter Messages): der Verlauf mit dieser Nummer als Sprechblasen und darunter die Antwort. Die
// Antwort ist der normale SMS-Komposer (Vorlagen, Zeichenzähler, Prüfungen, Senden planen), kein Sonderweg. Bei einer Nummer ohne
// Lead steht stattdessen die Suche nach dem Lead.
import { useCallback, useEffect, useRef, useState } from "react";
import { leadsApi, sucheApi, type Kontakt, type SuchTreffer } from "../../api";
import { useEchtzeit } from "../../api/echtzeit";
import { smsEingangApi, type SmsZeile } from "../../api/smsEingang";
import { SmsKomposer } from "../../kommunikation";
import { Laden, StatusPille } from "../Bausteine";
import { FehlerBox } from "../Fehler";
import { Icon } from "../Icon";
import { LeadAusNummer } from "./LeadAusNummer";
import { gegenstelle, nachrichtZeit, planZeit, STATUS_TEXT } from "./nachrichtenLogik";
import "./nachrichten.css";

interface Props {
  sms: SmsZeile;
  /** Antwort offen (Taste r) */
  antwortOffen: boolean;
  onAntwort: (offen: boolean) => void;
  /** nach dem Senden einer Antwort */
  onGesendet: () => void;
  onLead: (leadId: string) => void;
}

const ANZAHL = 15;

export function SmsVerlauf({ sms, antwortOffen, onAntwort, onGesendet, onLead }: Props) {
  const [verlauf, setVerlauf] = useState<SmsZeile[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [kontakte, setKontakte] = useState<Kontakt[] | null>(null);
  const [kontaktFehler, setKontaktFehler] = useState<unknown>(null);
  const ende = useRef<HTMLDivElement>(null);

  const laden = useCallback(async () => {
    try {
      const r = await smsEingangApi.liste(sms.lead_id ? { lead_id: sms.lead_id, limit: ANZAHL } : { nummer: sms.entfernte_nummer ?? undefined, limit: ANZAHL });
      // neueste zuerst geliefert; angezeigt wird die Unterhaltung von oben alt nach unten neu
      const alle = r.data.some((s) => s.id === sms.id) ? r.data : [...r.data, sms];
      // Geplante SMS stehen als gestrichelte Blasen am Ende, nach ihrem Zeitpunkt
      const gesendet = alle.filter((s) => s.status !== "geplant").sort((a, b) => (a.date_created ?? "").localeCompare(b.date_created ?? ""));
      const geplant = alle.filter((s) => s.status === "geplant").sort((a, b) => (a.geplant_at ?? "").localeCompare(b.geplant_at ?? ""));
      setVerlauf([...gesendet, ...geplant]);
      setFehler(null);
    } catch (e) {
      setFehler(e);
    }
  }, [sms]);

  useEffect(() => {
    void laden();
  }, [laden]);
  useEchtzeit(["sms.received", "sms.updated"], () => void laden());

  useEffect(() => {
    if (verlauf) ende.current?.scrollIntoView({ block: "nearest" });
  }, [verlauf?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!antwortOffen || !sms.lead_id || kontakte) return;
    let abgebrochen = false;
    leadsApi
      .detail(sms.lead_id)
      .then((d) => !abgebrochen && setKontakte(d.contacts))
      .catch((e) => !abgebrochen && setKontaktFehler(e));
    return () => {
      abgebrochen = true;
    };
  }, [antwortOffen, sms.lead_id, kontakte]);

  return (
    <div className="sms-verlauf" onClick={(e) => e.stopPropagation()}>
      {fehler ? (
        <FehlerBox fehler={fehler} kompakt onNochmal={() => void laden()} />
      ) : !verlauf ? (
        <Laden klein />
      ) : (
        <div className="sms-verlauf__blasen" role="log" aria-label={`Verlauf mit ${gegenstelle(sms)}`}>
          {verlauf.map((s) => (
            <Blase key={s.id} s={s} aktuell={s.id === sms.id} />
          ))}
          <div ref={ende} />
        </div>
      )}

      {sms.lead_id ? (
        antwortOffen ? (
          kontaktFehler ? (
            <FehlerBox fehler={kontaktFehler} kompakt />
          ) : !kontakte ? (
            <Laden klein />
          ) : (
            <SmsKomposer
              leadId={sms.lead_id}
              kontakte={kontakte}
              kontakt={sms.contact_id}
              onClose={() => onAntwort(false)}
              onGesendet={() => {
                onAntwort(false);
                void laden();
                onGesendet();
              }}
            />
          )
        ) : (
          <div className="sms-verlauf__fuss">
            <button type="button" className="knopf knopf--klein knopf--primaer" onClick={() => onAntwort(true)} title="Antworten (r)">
              <Icon name="sms" groesse={13} /> Antworten
            </button>
            <button type="button" className="knopf knopf--klein" onClick={() => onLead(sms.lead_id as string)} title="Lead öffnen (⌘ Enter)">
              Lead öffnen
            </button>
            <span className="sms-verlauf__hinweis leise">r antwortet · Esc schließt</span>
          </div>
        )
      ) : (
        <SmsOhneLead sms={sms} onLead={onLead} />
      )}
    </div>
  );
}

function Blase({ s, aktuell }: { s: SmsZeile; aktuell: boolean }) {
  const aus = s.richtung === "ausgehend";
  const geplant = s.status === "geplant";
  return (
    <div className={`sms-blase sms-blase--${aus ? "aus" : "ein"}${aktuell ? " sms-blase--aktuell" : ""}${geplant ? " sms-blase--geplant" : ""}`}>
      <div className="sms-blase__text">{s.text}</div>
      <div className="sms-blase__meta">
        {aus && s.nutzer_name ? <span>{s.nutzer_name} · </span> : null}
        {geplant ? <span className={s.ueberfaellig ? "text-gefahr" : undefined}>{s.ueberfaellig ? "Überfällig, geplant für " : "Geplant für "}{planZeit(s.geplant_at)}</span> : <span>{nachrichtZeit(s.date_created)}</span>}
        {aus && !geplant ? <span className={`sms-status sms-status--${s.status}`}> · {STATUS_TEXT[s.status]}</span> : null}
        {s.status === "fehlgeschlagen" && s.fehler_text ? <div className="sms-blase__fehler">{s.fehler_text}</div> : null}
      </div>
    </div>
  );
}

/** Nummer ohne Lead: passende Leads und Kontakte suchen, sonst einen Lead anlegen. */
function SmsOhneLead({ sms, onLead }: { sms: SmsZeile; onLead: (leadId: string) => void }) {
  const nummer = sms.entfernte_nummer ?? "";
  const [treffer, setTreffer] = useState<SuchTreffer[] | null>(null);

  useEffect(() => {
    if (!nummer) return;
    const abbruch = new AbortController();
    sucheApi
      .suchen(nummer, 8, abbruch.signal)
      .then((r) => {
        const gesehen = new Set<string>();
        setTreffer(r.data.filter((t) => (gesehen.has(t.lead_id) ? false : (gesehen.add(t.lead_id), true))));
      })
      .catch(() => !abbruch.signal.aborted && setTreffer([]));
    return () => abbruch.abort();
  }, [nummer]);

  return (
    <div className="sms-verlauf__fuss sms-verlauf__fuss--ohne-lead">
      <span className="leise">Die Nummer {sms.entfernte_anzeige ?? nummer} gehört zu keinem Lead.</span>
      {treffer?.map((t) => (
        <button key={`${t.lead_id}${t.contact_id ?? ""}`} type="button" className="knopf knopf--klein" onClick={() => onLead(t.lead_id)} title="Lead öffnen">
          {t.title}
          {t.status_label ? <StatusPille label={t.status_label} klein schlicht /> : null}
        </button>
      ))}
      {nummer ? <LeadAusNummer nummer={nummer} anzeige={sms.entfernte_anzeige ?? nummer} onAngelegt={(lead) => onLead(lead.id)} /> : null}
    </div>
  );
}
